import _ from 'lodash';
import unitSystem from '@/features/unitSystems';
import { Action } from '@/OptimusRoutes/components/mapDevices/store/actions';
import {
	DevicesCoreData,
	MapDevices,
	Selected,
	SelectedDates,
} from '@/OptimusRoutes/components/mapDevices/types/mapDevicesReducer.types';
import {
	END_OF_DAY_STANDARD_FORMAT,
	STANDARD_DATE_FORMAT,
	START_OF_DAY_STANDARD_FORMAT,
	UTC_FORMAT,
	YEAR_MONTH_DAY_TIME_FORMAT,
} from '@/OptimusRoutes/constants';
import {
	LAST_EVENTS_LENGTH_SIZE,
	SELECT_ALL_GROUPS,
	SELECT_ALL_UNITS,
} from '@/OptimusRoutes/pages/MainMap/components/ControlPanel/constants';
import { EventHistoryByDeviceDto } from '@/OptimusRoutes/pages/MainMap/types/trackingHistory.types';
import { ActionType } from '@/types/actionType';
import {
	dateToString,
	getDifference,
	getTimeDifference,
	getUtc,
	isNewDate,
	isToday,
	utcToLocalIana,
	utcToLocalIanaWithFormat,
} from '@/utilities/dateUtils';

type State = {
	/**
	 * devices is the Complete object of all the devices of the user with all its data
	 * e.g. {
	 * 		111: {
	 * 			id: 111,
	 * 				id: 111,
	 * 				pin: '1234567890'.
	 * 				description: 'John Doe Truck'
	 * 				shared: false,
	 * 				latitude: -88.432
	 * 		},
	 * 		...
	 * }
	 *
	 * Contains all device information including:
	 * - Device information (id, name, description)
	 * - Real-time telemetry (speed, latitude, longitude, sensors, speed, idle, etc...)
	 * - Other (signal, camera, video, trackingHistoryId, battery, etc...)
	 *
	 * Indexed by device ID for efficient lookups
	 */
	devices: MapDevices;
	/**
	 * devicesCoreData is a Lightweight device data for performance-critical operations
	 *
	 * Contains minimal essential data to prevent unnecessary component re-renders:
	 * - Device ID for identification
	 * - Pin for rabbit subscription
	 * - Description for UI labels
	 * - isTransmitting status for real-time indicators & UI elements
	 *
	 * This separation allows UI components to subscribe to only the data they need,
	 * improving performance by avoiding re-renders when heavy telemetry data changes (lat, lng, speed...)
	 */
	devicesCoreData: DevicesCoreData;
	/**
	 * Checks if all units are being fetched
	 */
	isLoadingUnits: boolean | null;
	isLoadingSegmentedTrail: boolean;
	/**
	 * mainMapSelected is the object containing the selected unit or units (if `Select All` is selected)
	 */
	mainMapSelected: Selected;
	/**
	 * segmented is the flags that tells if the trail on the map will be segmented or not.
	 */
	isSegmented: boolean;
	/**
	 * Date of the trail that will be shown
	 * ⚠️: Date is on browser's Timezone. (Do not change)
	 */
	selectedDates: SelectedDates | null;
	isHistoryOpen: boolean;
};

const initialState: State = {
	devices: {},
	devicesCoreData: {},
	isLoadingUnits: null,
	isLoadingSegmentedTrail: false,
	isSegmented: false,
	selectedDates: null,
	mainMapSelected: {
		groups: {
			[SELECT_ALL_GROUPS]: {
				total: 0,
				units: [],
			},
		},
		unitId: null,
		hasTrailData: false,
	},
	isHistoryOpen: false,
};

const reducer = (state: State = initialState, action: Action): State => {
	switch (action.type) {
		case ActionType.MAP_DEVICES_SET_DEVICES:
			return {
				...state,
				devices: action.payload.devices,
				devicesCoreData: action.payload.devicesCoreData,
			};
		/** Updates from Rabbit
		 * This is the reducer that updates the incoming data from rabbit
		 ** ⚠️: Rabbit timestamps are in UTC, so (for this specific reducer) it may be easiest to work with dates in UTC.
		 */
		case ActionType.MAP_DEVICES_UPDATE_DEVICE: {
			const payload = action.payload;
			if (!payload) {
				return state;
			}
			const device = state.devices[payload.id];
			if (!device) {
				return state;
			}

			const isMultimediaEvent =
				payload.eventsToUpdate?.[0]?.data?.type === 'IMAGE' ||
				payload.eventsToUpdate?.[0]?.data?.type === 'VIDEO';

			const incomingUtcDate = payload.data?.utcDate;
			if (!incomingUtcDate) {
				return state;
			}

			let shouldAddEventToStore = false;

			const isIncomingDateNew = device.utcDate
				? getTimeDifference(device.utcDate, incomingUtcDate, 'seconds') > 0
				: true;

			if (isMultimediaEvent) {
				const lastStoredEvent = device.lastEvents?.[0];

				if (lastStoredEvent) {
					const isDuplicatedEvent =
						Number(lastStoredEvent.trackingHistoryId) === Number(payload.data?.trackingHistoryId) &&
						lastStoredEvent.eventId === payload.eventsToUpdate?.[0]?.id;
					const isRecentEvent = getTimeDifference(lastStoredEvent.date, incomingUtcDate, 'seconds') >= 0;
					shouldAddEventToStore = !isDuplicatedEvent && isRecentEvent;

					if (isDuplicatedEvent || !isRecentEvent) {
						return state;
					}
				} else {
					const todaysUtc = dateToString(getUtc(), 'YYYY-MM-DDTHH:mm:ss');
					const isRecentEvent = getTimeDifference(todaysUtc, incomingUtcDate, 'days') <= 1;

					if (!isRecentEvent) {
						return state;
					}

					shouldAddEventToStore = true;
				}
			} else {
				const isSameMessageType = device.lastMessageType === payload.lastMessageType;

				if (!isIncomingDateNew && isSameMessageType) {
					return state;
				}
			}

			const { id: deviceId, data, eventsToUpdate } = action.payload;
			const {
				tanks: newTanks,
				thermometers: newThermometer,
				utcDate,
				latitude,
				longitude,
				...restOfPayload
			} = data;

			const lastReportedDate = state.devices[deviceId]?.lastReportedEvent?.utcDate;
			if (!isMultimediaEvent) {
				shouldAddEventToStore = isNewDate(utcDate, lastReportedDate);
			}
			let lastEvents: EventHistoryByDeviceDto[] = state.devices[deviceId]?.lastEvents || [];
			const hasEventsToUpdate = shouldAddEventToStore && eventsToUpdate && eventsToUpdate?.length > 0;
			const ianaTimezone = state.devices[deviceId].ianaTimezone;

			if (hasEventsToUpdate && data?.latitude && data?.longitude && data?.trackingHistoryId) {
				const eventsWithLocalDatetime: EventHistoryByDeviceDto[] = eventsToUpdate?.map((event) => ({
					eventId: event.id,
					date: dateToString(utcToLocalIana({ utcDate, ianaTimezone }), UTC_FORMAT),
					trackingHistoryId: data.trackingHistoryId?.toString()!,
					data: event.data,
					latitude: data.latitude!,
					longitude: data.longitude!,
				}));

				const preExistentEvents = lastEvents.length > 0;
				if (preExistentEvents) {
					const previousEventDate = dateToString(lastEvents[0].date, STANDARD_DATE_FORMAT);
					const newEventDate = dateToString(
						eventsWithLocalDatetime[eventsWithLocalDatetime.length - 1].date,
						STANDARD_DATE_FORMAT
					);
					const difference = getDifference(previousEventDate, newEventDate, 'days');
					const eventsOnSameDate = difference === 0;

					lastEvents = eventsOnSameDate
						? [...eventsWithLocalDatetime, ...lastEvents]
						: eventsWithLocalDatetime;
				} else {
					lastEvents = eventsWithLocalDatetime;
				}
				lastEvents = lastEvents
					.sort((a, b) => +new Date(b.date) - +new Date(a.date))
					.slice(0, LAST_EVENTS_LENGTH_SIZE);
			}

			let thermometers = state.devices[deviceId]?.thermometers || {};
			thermometers = {
				...thermometers,
				...action.payload.data.thermometers,
			};

			const oldTanks = state.devices[deviceId]?.tanks;
			const oldThermometers = state.devices[deviceId]?.thermometers;

			let tanks = state.devices[deviceId]?.tanks || {};
			tanks = { ...tanks, ...newTanks };

			let deviceLocation = {};
			if (state.devices[deviceId]?.latitude && state.devices[deviceId]?.longitude && latitude && longitude) {
				const previousDeviceLocation = {
					latitude: state.devices[deviceId]?.latitude,
					longitude: state.devices[deviceId]?.longitude,
				};

				deviceLocation = isIncomingDateNew
					? { latitude: latitude, longitude: longitude }
					: previousDeviceLocation;
			}

			const updatedUtc = utcDate || state.devices[deviceId].utcDate;
			let updatedState = {
				...state,
				devices: {
					...state.devices,
					[deviceId]: {
						...state.devices[deviceId],
						...restOfPayload,
						...deviceLocation,
						utcDate: updatedUtc,
						lastEvents,
						date: utcToLocalIanaWithFormat(updatedUtc, ianaTimezone),
					},
				},
			};

			const selectedDeviceId = state.mainMapSelected.unitId;
			if (selectedDeviceId && selectedDeviceId === deviceId) {
				const ianaTimezone = state.devices[action.payload.id].ianaTimezone;
				const localDateTime = utcToLocalIana({ utcDate: action.payload.data.utcDate, ianaTimezone });
				const localDate = dateToString(localDateTime, STANDARD_DATE_FORMAT);
				const newSelectedDates = {
					initial: `${localDate} 00:00:00`,
					final: `${localDate} 23:59:59`,
				};
				const isSameDate = _.isEqual(newSelectedDates, state.selectedDates);
				if (!isSameDate) {
					updatedState.selectedDates = newSelectedDates;
				}
			}

			const isSameTanks = _.isEqual(oldTanks, newTanks);
			if (newTanks && !isSameTanks) {
				updatedState.devices[deviceId].tanks = tanks;
			}

			const isSameThermometers = _.isEqual(oldThermometers, newThermometer);
			if (newThermometer && !isSameThermometers) {
				updatedState.devices[deviceId].thermometers = thermometers;
			}

			const deviceToUpdateData = updatedState.devicesCoreData[deviceId];
			const hasNotBeenTransmitting = action.payload.data?.utcDate && !deviceToUpdateData?.isTransmiting;
			if (hasNotBeenTransmitting) {
				console.log('🔴 New Transmitting Device ', deviceToUpdateData.description); // eslint-disable-line no-console
				let groups = state.mainMapSelected.groups;
				if (state.mainMapSelected.groups?.[SELECT_ALL_GROUPS]) {
					const selectAllGroup = state.mainMapSelected.groups?.[SELECT_ALL_GROUPS];
					groups = {
						...groups,
						[SELECT_ALL_GROUPS]: {
							...selectAllGroup,
							units: [...selectAllGroup.units, deviceToUpdateData.id],
						},
					};
				}
				updatedState = {
					...updatedState,
					devicesCoreData: {
						...updatedState.devicesCoreData,
						[deviceId]: {
							...updatedState.devicesCoreData[deviceId],
							isTransmiting: true,
						},
					},
					mainMapSelected: {
						...state.mainMapSelected,
						groups,
					},
				};
			}

			return updatedState;
		}
		case ActionType.MAP_DEVICES_UPDATE_HISTORICAL_MARKER: {
			const payload = action.payload;
			if (!payload) {
				return state;
			}
			const deviceId = payload.id;
			const dataToUpdate = payload.data;

			if (!state.devices[deviceId]) {
				return state;
			}

			return {
				...state,
				devices: {
					...state.devices,
					[deviceId]: {
						...state.devices[deviceId],
						...dataToUpdate,
					},
				},
			};
		}
		case ActionType.MAP_DEVICES_UPDATE_LAST_REPORT: {
			const selectedDevice = state.devices[action.payload.id];
			const date = action.payload.data.date || selectedDevice.date;

			const localDate = dateToString(date, STANDARD_DATE_FORMAT);
			const selectedDates = {
				initial: `${localDate} 00:00:00`,
				final: `${localDate} 23:59:59`,
			};
			return {
				...state,
				devices: {
					...state.devices,
					[action.payload.id]: {
						...selectedDevice,
						...action.payload.data,
					},
				},
				selectedDates,
			};
		}
		case ActionType.MAP_DEVICES_SET_LOADING_STATUS:
			return {
				...state,
				isLoadingUnits: action.payload,
			};
		case ActionType.MAP_DEVICES_SET_LOADING_SEGMENTED_TRAIL:
			return {
				...state,
				isLoadingSegmentedTrail: action.payload,
			};
		case ActionType.MAP_DEVICES_CLEAR_DEVICES:
			return {
				...state,
				devices: {},
				devicesCoreData: {},
				mainMapSelected: {
					...state.mainMapSelected,
					groups: {
						[SELECT_ALL_GROUPS]: {
							total: 0,
							units: [],
						},
					},
				},
			};
		case ActionType.MAP_DEVICES_CLEAR_ALL_DATA:
			return initialState;
		case ActionType.MAP_DEVICES_SET_LAST_EVENTS: {
			const { deviceId, lastEvents: eventsList } = action.payload;
			const ianaTimezone = state.devices[deviceId].ianaTimezone;
			let lastEvents = state.devices[deviceId]?.lastEvents || [];

			const previousEventExist = lastEvents.length > 0;
			if (previousEventExist) {
				const preExistentEventsDate = unitSystem.getDateTimeWithOffsetName(
					lastEvents[lastEvents.length - 1].date,
					ianaTimezone,
					STANDARD_DATE_FORMAT
				);
				const selectedDate = dateToString(state.selectedDates?.initial, YEAR_MONTH_DAY_TIME_FORMAT);
				const hasEventsForSelectedDate = getDifference(preExistentEventsDate, selectedDate, 'days') === 0;

				if (!hasEventsForSelectedDate) {
					lastEvents = eventsList;
				}
			} else {
				lastEvents = eventsList;
			}

			return {
				...state,
				devices: {
					...state.devices,
					[deviceId]: {
						...state.devices[deviceId],
						lastEvents,
					},
				},
			};
		}
		/**
		 * * Start of Main Map Specific Reducers
		 */
		case ActionType.MAP_DEVICES_SET_SELECTED_GROUPS: {
			if (_.isEqual(action.payload, state.mainMapSelected.groups)) {
				return state;
			}
			console.log('🎯👥 Selecting new group units ', action.payload); // eslint-disable-line no-console

			const updatedState = {
				...state,
				mainMapSelected: {
					...state.mainMapSelected,
					groups: { ...state.mainMapSelected.groups, ...action.payload },
				},
			};
			return updatedState;
		}
		case ActionType.MAP_DEVICES_SET_SELECTED_UNIT: {
			const selectedUnitId = action.payload;
			if (selectedUnitId === state.mainMapSelected.unitId) {
				return state;
			}
			const previouslySelectedUnitId = Number(state.mainMapSelected?.unitId);

			// clear panel data
			if (previouslySelectedUnitId && state.devices[previouslySelectedUnitId]?.lastEvents) {
				const clearedPreviousPanelData = {
					lastEvents: [],
					speed: null,
					tanks: null,
					thermometers: null,
					battery: undefined,
					batteryVoltage: undefined,
					lastReportedEvent: undefined,
				};
				state.devices[previouslySelectedUnitId] = {
					...state.devices[previouslySelectedUnitId],
					...clearedPreviousPanelData,
				};
			}

			let selectedDates: SelectedDates | null = state.selectedDates;
			if (selectedUnitId === SELECT_ALL_UNITS) {
				selectedDates = null;
			} else {
				const date = state.devices[+selectedUnitId].date;
				const ianaTimezone = state.devices[+selectedUnitId].ianaTimezone;
				const localDate = unitSystem.getDateTimeWithOffsetName(date, ianaTimezone, STANDARD_DATE_FORMAT);

				selectedDates = {
					initial: `${localDate} 00:00:00`,
					final: `${localDate} 23:59:59`,
				};
			}

			const updatedState = {
				...state,
				selectedDates,
				isSegmented: false,
				mainMapSelected: {
					...state.mainMapSelected,
					unitId: selectedUnitId,
					trailSegmentIndex: null,
				},
			};
			return updatedState;
		}
		case ActionType.MAP_DEVICES_SET_SELECTED_GROUP_CHILD: {
			const newGroups = { ...state.mainMapSelected.groups };
			const updatedGroup = newGroups[action.payload.groupId];
			if (updatedGroup) {
				updatedGroup.units = Array.from(new Set([...updatedGroup.units, action.payload.childId]));
			} else {
				newGroups[action.payload.groupId] = {
					total: action.payload.total ?? 0,
					units: [action.payload.childId],
				};
			}
			console.log('🎯👥 Selecting new group units CHILD ', action.payload); // eslint-disable-line no-console

			const updatedState = {
				...state,
				mainMapSelected: {
					...state.mainMapSelected,
					groups: newGroups,
				},
			};
			return updatedState;
		}
		case ActionType.MAP_DEVICES_REMOVE_SELECTED_GROUP: {
			if (state.mainMapSelected.groups && state.mainMapSelected.groups[action.payload]) {
				const newGroups = { ...state.mainMapSelected.groups };
				delete newGroups[action.payload];

				const noGroupsSelected = Object.keys(newGroups).length === 0;

				let selectedUnit = state.mainMapSelected.unitId;
				if (noGroupsSelected) {
					selectedUnit = SELECT_ALL_UNITS;
				}

				return {
					...state,
					mainMapSelected: {
						unitId: selectedUnit,
						groups: newGroups,
					},
				};
			}
			return state;
		}
		case ActionType.MAP_DEVICES_REMOVE_SELECTED_GROUP_CHILD: {
			const selectedGroups = { ...state.mainMapSelected.groups };
			const updatedUnits = selectedGroups[action.payload.groupId]?.units.filter(
				(unitId) => unitId !== action.payload.childId
			);
			if (updatedUnits.length === 0) {
				delete selectedGroups[action.payload.groupId];
			} else {
				selectedGroups[action.payload.groupId].units = updatedUnits;
			}

			const updatedState = {
				...state,
				mainMapSelected: {
					...state.mainMapSelected,
					groups: selectedGroups,
				},
			};
			return updatedState;
		}
		case ActionType.MAP_DEVICES_UPDATE_TRAIL_DATA: {
			const payload = action.payload;

			let isSegmented = state.isSegmented;
			let trailSegmentIndex = state.mainMapSelected?.trailSegmentIndex;
			let hasTrailData = payload?.hasTrailData ?? state.mainMapSelected?.hasTrailData;

			if (payload?.trailSegmentIndex !== null && payload?.trailSegmentIndex !== undefined) {
				isSegmented = true;
				trailSegmentIndex = payload?.trailSegmentIndex;
			}

			return {
				...state,
				isSegmented,
				mainMapSelected: {
					...state.mainMapSelected,
					trailSegmentIndex,
					hasTrailData,
				},
			};
		}
		/**
		 * * End of Main Map Specific Reducers
		 */
		case ActionType.MAP_DEVICES_SET_SELECTED_DATE: {
			const localDateTime = action.payload;

			const newSelectedDates = {
				initial: dateToString(localDateTime, START_OF_DAY_STANDARD_FORMAT),
				final: dateToString(localDateTime, END_OF_DAY_STANDARD_FORMAT),
			};
			const isSameDate = _.isEqual(newSelectedDates, state.selectedDates);
			if (isSameDate) {
				return {
					...state,
				};
			}

			let trailSegmentUpdate = {};
			let isHistoryOpen = state.isHistoryOpen;
			if (isToday(localDateTime)) {
				isHistoryOpen = false;
				trailSegmentUpdate = { trailSegmentIndex: null };
			} else {
				isHistoryOpen = true;
			}

			return {
				...state,
				selectedDates: newSelectedDates,
				isSegmented: false,
				mainMapSelected: {
					...state.mainMapSelected,
					...trailSegmentUpdate,
					trailSegmentIndex: null,
				},
				isHistoryOpen,
			};
		}

		case ActionType.MAP_DEVICES_UPDATE_DEVICE_PROPERTIES: {
			const { id, ...properties } = action.payload;

			return {
				...state,
				devices: {
					...state.devices,
					[id]: {
						...state.devices[id],
						...properties,
					},
				},
				devicesCoreData: {
					...state.devicesCoreData,
					[id]: {
						...state.devicesCoreData[id],
						...properties,
					},
				},
			};
		}

		default:
			return state;
	}
};

export default reducer;
